import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { SELECTABLE_THEME_IDS, ThemeId } from '../../constants/themes';
import { useSelectedTheme, HOME_SELECTION } from '../../hooks/useSelectedTheme';
import { useLanguage } from '../../i18n';

/**
 * Colonne de navigation à gauche sur desktop, bande horizontale sur mobile :
 * même balisage, seul le CSS change. Présente sur la grille comme sur le mode
 * vote, d'où son emplacement dans la coque de l'application.
 */
const ThemeSidebar: React.FC = () => {
  const { t } = useLanguage();
  const { pathname } = useLocation();
  const { selection, themes, select } = useSelectedTheme();

  const isVoteMode = pathname === '/vote';
  const isHome = !isVoteMode && selection === HOME_SELECTION;

  const batchCountOf = (id: ThemeId): number =>
    themes.find(theme => theme.id === id)?.batchCount ?? 0;

  // Ordre alphabétique sur le libellé traduit.
  const orderedThemeIds = [...SELECTABLE_THEME_IDS].sort((a, b) =>
    t.themes[a].localeCompare(t.themes[b]),
  );

  return (
    <nav className="theme-sidebar" aria-label={t.feed.themesTitle}>
      <div className="theme-sidebar-scroll">
        <ul className="theme-sidebar-list">
          <li>
            <button
              type="button"
              className={`theme-sidebar-item ${isHome ? 'selected' : ''}`}
              aria-current={isHome ? 'true' : undefined}
              onClick={() => select(HOME_SELECTION)}
            >
              {t.feed.home}
            </button>
          </li>

          {/* Mode de jeu à part, pas un thème : le vote sur le lot d'un seul
              créateur, qui était l'accueil avant la grille. */}
          <li>
            <Link
              to="/vote"
              className={`theme-sidebar-item ${isVoteMode ? 'selected' : ''}`}
              aria-current={isVoteMode ? 'page' : undefined}
            >
              {t.feed.voteMode}
            </Link>
          </li>

          <li className="theme-sidebar-separator" role="presentation" />

          {orderedThemeIds.map(id => {
            const isSelected = !isVoteMode && id === selection;
            const isEmpty = batchCountOf(id) === 0;

            return (
              <li key={id}>
                <button
                  type="button"
                  className={`theme-sidebar-item ${isSelected ? 'selected' : ''} ${isEmpty ? 'empty' : ''}`}
                  aria-current={isSelected ? 'true' : undefined}
                  onClick={() => select(id)}
                >
                  {t.themes[id]}
                </button>
              </li>
            );
          })}
        </ul>

        {/* La grille n'a pas de footer : ses liens légaux vivent ici, en bas de
            la colonne sur desktop et en fin de bande sur mobile. */}
        <div className="theme-sidebar-legal">
          <Link to="/privacy" className="theme-sidebar-legal-link">{t.footer.privacy}</Link>
          <Link to="/terms" className="theme-sidebar-legal-link">{t.footer.terms}</Link>
        </div>
      </div>
    </nav>
  );
};

export default ThemeSidebar;
